import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import styles from './Divider.module.css';

// ── CVA definition ─────────────────────────────────────────────────────────

const divider = cva(styles.base, {
  variants: {
    orientation: {
      horizontal: styles.horizontal,
      vertical: styles.vertical,
    },
    dashed: {
      true: styles.dashed,
      false: styles.solid,
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
    dashed: false,
  },
});

// ── Types ──────────────────────────────────────────────────────────────────

interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Direction of the divider line */
  orientation?: 'horizontal' | 'vertical';
  /** Use dashed line instead of solid */
  dashed?: boolean;
  /** Text label rendered in the middle of a horizontal divider */
  children?: React.ReactNode;
  /** Where the text sits: left, center, right */
  textAlign?: 'left' | 'center' | 'right';
}

/**
 * Divider — visual separator between content sections.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | orientation | 'horizontal' \| 'vertical' | 'horizontal' | Line direction |
 * | dashed | boolean | false | Dashed line style |
 * | children | ReactNode | — | Text label (horizontal only) |
 * | textAlign | 'left' \| 'center' \| 'right' | 'center' | Where the label sits |
 *
 * @example
 * <Divider />
 * <Divider dashed />
 * <Divider>Section</Divider>
 * <Divider orientation="vertical" />
 */
export function Divider({
  orientation = 'horizontal',
  dashed = false,
  textAlign = 'center',
  children,
  style,
  className,
  ...props
}: DividerProps) {
  // Vertical divider
  if (orientation === 'vertical') {
    return (
      <div
        role="separator"
        className={divider({ orientation: 'vertical', dashed, className })}
        style={style}
        {...props}
      />
    );
  }

  // Horizontal with text label
  if (children) {
    // The left-line and right-line flex values are runtime-computed from textAlign —
    // they form a conditional expression with no finite class set. Extracted as
    // variables (not JSX ObjectExpression literals) per ESLint bare-identifier exception.
    const leftLineFlex = textAlign === 'left' ? '0 0 5%' : 1;
    const rightLineFlex = textAlign === 'right' ? '0 0 5%' : 1;
    const leftLineStyle: React.CSSProperties = { flex: leftLineFlex };
    const rightLineStyle: React.CSSProperties = { flex: rightLineFlex };

    return (
      <div
        role="separator"
        className={clsx(styles.base, styles.withLabel, dashed ? styles.dashed : styles.solid, className)}
        style={style}
        {...props}
      >
        <div className={styles.line} style={leftLineStyle} />
        <span className={styles.labelText}>
          {children}
        </span>
        <div className={styles.line} style={rightLineStyle} />
      </div>
    );
  }

  // Plain horizontal divider
  return (
    <div
      role="separator"
      className={divider({ orientation: 'horizontal', dashed, className })}
      style={style}
      {...props}
    />
  );
}
